/**
 * Benchmarks API — Chunk 83: Portfolio Benchmarking & Index Comparison
 * Extended in Chunk 158: custom date range (from/to) and SP500 alias support
 */

import apiClient from './client';

export type BenchmarkId = 'SPX' | 'BTC' | 'ETH' | 'TOTAL3' | 'SP500';
export type BenchmarkRange = '3m' | '6m' | '1y' | 'all';

export interface BenchmarkSeriesPoint {
  date: string;
  portfolio: number;
  benchmark: number | null;
}

export interface BenchmarkCompareResult {
  userId: string;
  benchmark: BenchmarkId;
  range: BenchmarkRange | 'custom';
  /** Set when custom date range was used */
  from?: string;
  to?: string;
  series: BenchmarkSeriesPoint[];
  /** Portfolio total return minus benchmark total return (percentage points) */
  alpha: number | null;
  /** Beta coefficient (covariance / benchmark variance) */
  beta: number | null;
  /** Pearson correlation of daily returns */
  correlation: number | null;
  /** Simple portfolio % return over the window (Chunk 158) */
  portfolioReturnPct?: number | null;
  /** Simple benchmark % return over the window (Chunk 158) */
  benchmarkReturnPct?: number | null;
  /** Portfolio USD value at window start (Chunk 158) */
  portfolioStartValue?: number | null;
  /** Portfolio USD value at window end (Chunk 158) */
  portfolioEndValue?: number | null;
  message?: string;
  generatedAt: string;
}

/**
 * Fetch portfolio vs benchmark comparison time-series for a user.
 * Both series are indexed to 100 at the start of the requested range.
 *
 * @param userId    - User ID
 * @param benchmark - Benchmark to compare against (default: 'BTC')
 * @param range     - Preset time range (default: '1y'); ignored when from+to provided
 * @param from      - ISO date string for custom range start (optional, Chunk 158)
 * @param to        - ISO date string for custom range end (optional, Chunk 158)
 */
export async function getBenchmarkComparison(
  userId: string,
  benchmark: BenchmarkId = 'BTC',
  range: BenchmarkRange = '1y',
  from?: string,
  to?: string,
): Promise<BenchmarkCompareResult> {
  const params = new URLSearchParams({ benchmark, range });
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const res = await apiClient.get<BenchmarkCompareResult>(
    `/analytics/benchmarks/compare/${userId}?${params.toString()}`,
  );
  return res.data;
}
